import React, { FC, useCallback, useMemo, useState } from 'react';
import { DndContext, DragEndEvent, DragMoveEvent, DragOverlay, DragStartEvent, Modifier } from '@dnd-kit/core';
import dayjs from 'dayjs';
import { Draggable } from '../dndComponents/Draggable';
import { ECalendarMode, ECalendarView, HandleDropResult, IDndProviderProps, IResource, OnSlotDrop } from '../types';
import {
	DEFAULT_DND_STEP_LENGTH,
	getGridStepSize,
	oneHourHeight,
	oneHourWidth,
} from '../utils/timeUtils';
import { useMiddlewareContext } from '../hooks/useMiddlewareContext';
import { createSnapModifier } from '@dnd-kit/modifiers';
import { ISlot } from '../../organisms/bo_calendar_slot/types';
import { DateTimeFormats, isSameDate, TimeUnit } from '@bondsports/date-time';
import { DRAGGABLE_ID_SEPARATOR } from '../utils/consts';
import { useResources } from '../hooks/useResources';
import { CHARACHERS } from '../../lib/constants';
import { useCalendarStoreSelector } from '../hooks/useCalendarStoreSelector';
import { handleDailyDrop, handleDayXDrop, handleWeeklyDrop, isDailyMode } from '../utils/dnd';

export const DndProvider: FC<IDndProviderProps> = ({
	children,
	DragOverlayComponent,
	onChange,
	horizontal,
	view = ECalendarView.VERTICAL,
}) => {
	const { options, handleDrop } = useMiddlewareContext();
	const { resources } = useResources();

	const flattenedResources: IResource[] = useMemo(() => {
		return resources.flatMap(resource => (resource.children.length ? [resource, ...resource.children] : resource));
	}, [resources]);

	const getSlotsByResourceId = useCalendarStoreSelector(state => state.getSlotsByResourceId);

	const { mode = ECalendarMode.DAILY } = options;

	const [draggableItem, setDraggableItem] = useState<ISlot | undefined>();
	const [deltas, setDeltas] = useState(0);
	const [didMove, setDidMove] = useState(false);

	const [movingDelta, setMovingDelta] = useState(0);
	const debouncedDelta = movingDelta - deltas;

	const stepLength = options?.dndStepLength ?? DEFAULT_DND_STEP_LENGTH;
	const hourSizeForSnap =
		(horizontal ? options?.hourSize?.horizontal : options?.hourSize?.vertical) ??
		(horizontal ? oneHourWidth : oneHourHeight);
	const gridSize = getGridStepSize(hourSizeForSnap, stepLength);

	const modifiers: Modifier[] = useMemo(() => [createSnapModifier(gridSize)], [gridSize]);

	const handleDragEnd = useCallback(
		({ delta, active, over }: DragEndEvent) => {
			if (!draggableItem) return;

			let startDate: string | undefined;
			let endDate: string | undefined;
			let overId: string | number = over?.id || 0;

			let daysDifference: number = 0;
			let minutesDifference: number = 0;

			const currentDelta: number = view === ECalendarView.VERTICAL ? delta.y : delta.x;
			const size: number = view === ECalendarView.VERTICAL ? oneHourHeight : oneHourWidth;

			let dropResult: HandleDropResult;
			switch (mode) {
				case ECalendarMode.DAILY: {
					dropResult = handleDailyDrop(draggableItem, currentDelta - deltas, size, options.dndStepLength!);
					break;
				}
				case ECalendarMode.DAYX: {
					const [resourceId, date]: string[] = over?.id?.split(DRAGGABLE_ID_SEPARATOR) || [];
					const [, , originalDate]: string[] = active?.id?.split(DRAGGABLE_ID_SEPARATOR) || [];

					dropResult = handleDayXDrop(
						draggableItem,
						originalDate,
						date,
						currentDelta - deltas,
						size,
						options.dndStepLength!
					);
					daysDifference = dayjs(date).diff(originalDate, TimeUnit.DAY);
					overId = resourceId || 0;
					break;
				}
				case ECalendarMode.WEEKLY: {
					const [resourceId, date]: string[] = over?.id.split(DRAGGABLE_ID_SEPARATOR)!;
					dropResult = handleWeeklyDrop(draggableItem, date);
					overId = resourceId || 0;
					break;
				}
				default:
					return;
			}

			startDate = dropResult.startDate;
			endDate = dropResult.endDate;
			minutesDifference = dropResult.minutesDifference!;

			// do not allow to drop on the same resource in weekly view
			if (
				mode === ECalendarMode.WEEKLY &&
				Number(draggableItem.resourceId) === Number(overId) &&
				isSameDate(startDate, draggableItem.startDate, 'date')
			) {
				return;
			}

			const newResource: IResource = flattenedResources.find(resource => resource.id === Number(overId))!;
			const resource: IResource = flattenedResources.find(
				resource => resource.id === Number(draggableItem.resourceId)
			)!;

			if (!newResource || !resource || newResource?.resourceType !== resource?.resourceType) {
				return;
			}

			const slots: ISlot[] = getSlotsByResourceId(Number(overId));
			const slotAlreadyExists: boolean = slots.some(slot => slot.id === draggableItem.id);

			if (slotAlreadyExists && resource.id !== newResource.id) {
				return;
			}

			const onChangeData: OnSlotDrop = {
				type: 'SLOT_DROP',
				data: {
					startDate: dayjs.utc(startDate).format(DateTimeFormats.YYYY_MM_DD),
					endDate: dayjs.utc(endDate).format(DateTimeFormats.YYYY_MM_DD),
					startTime: dayjs.utc(startDate).format(DateTimeFormats.H24_WITH_SECONDS),
					endTime: dayjs.utc(endDate).format(DateTimeFormats.H24_WITH_SECONDS),
					overId: String(overId),
					parentId: String(draggableItem.spaceId),
					resourceId: String(draggableItem.resourceId),
					slotId: String(draggableItem.id),
					differenceInMinutes: minutesDifference,
				},
			};

			const answer: boolean | undefined | void = onChange?.(onChangeData);
			const result: boolean = typeof answer === 'boolean' ? answer : true;

			if (result) {
				// get the parentId from the active element
				const [parentId]: string[] = active.data?.current?.parentId.split(CHARACHERS.UNDERSCORE) ?? [];

				// get the slotId from the active element
				const [slotId] = active.id.split(DRAGGABLE_ID_SEPARATOR);

				const updatedSlot: ISlot = {
					...draggableItem,
					startDate: onChangeData!.data.startDate,
					startTime: onChangeData!.data.startTime,
					endDate: onChangeData!.data.endDate,
					endTime: onChangeData!.data.endTime,
					resourceId: String(onChangeData.data.overId),
				};

				handleDrop(
					updatedSlot,
					Number(slotId),
					Number(parentId),
					Number(overId),
					startDate,
					minutesDifference,
					daysDifference,
					endDate
				);
			}

			setDidMove(false);
			setDraggableItem(undefined);
		},
		[
			handleDrop,
			onChange,
			draggableItem,
			deltas,
			mode,
			options.dndStepLength,
			view,
			flattenedResources,
			getSlotsByResourceId,
		]
	);

	const handleDragMove = useCallback(
		({ delta }: DragMoveEvent) => {
			const isVertical: boolean = view === ECalendarView.VERTICAL;

			if (!didMove && isDailyMode(mode)) {
				setDeltas(isVertical ? delta.y : delta.x);
				setDidMove(true);
			}

			setMovingDelta(isVertical ? delta.y : delta.x);
		},
		[didMove, mode, view]
	);

	const onDragStart = useCallback(({ active }: DragStartEvent) => {
		setDraggableItem(active?.data?.current?.event);
	}, []);

	const onDragCancel = useCallback(() => {
		setDraggableItem(undefined);
	}, []);

	return (
		<DndContext
			modifiers={modifiers}
			onDragStart={onDragStart}
			onDragEnd={handleDragEnd}
			onDragMove={handleDragMove}
			onDragCancel={onDragCancel}
		>
			{children}
			<DragOverlay>
				{draggableItem && (
					<Draggable
						item={draggableItem}
						delta={debouncedDelta}
						Component={DragOverlayComponent}
						view={view}
						mode={mode}
					/>
				)}
			</DragOverlay>
		</DndContext>
	);
};

DndProvider.displayName = 'DndProvider';
